import type { ColumnFiltersModel } from './index';
import { computed } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { getActiveColumnFilters } from '@thinkalpha/ui-table/interactions/columnFilters';
import type { ActiveColumnFilter, ColumnFilterMatch } from '@thinkalpha/ui-table/interactions/columnFilters';
import { columnFiltersToTableFormula } from 'src/features/table/columnFilters/columnFilterFormula';
import type { FilterableColumn } from 'src/features/table/columnFilters/columnFilterFormula';
import type { ColumnFiltersSliceEvents, ColumnFiltersSliceSignals } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';

export type ColumnFiltersEventBus = EventBus<ColumnFiltersSliceSignals, ColumnFiltersSliceEvents>;

/** What a table widget root derives from its session column filters (ADR 0014, Column Filter Contract). */
export interface TableColumnFilters {
    /** The widget's rollout flag; off means no column filter applies (AC-FILTER-009). */
    readonly enabled: ReadonlySignal<boolean>;
    /** Per-column eligibility for the grid header, replaced whenever eligibility changes. */
    readonly canFilterColumn: ReadonlySignal<(columnId: string) => boolean>;
    /** The matches the table can apply per column, for the editor; empty while filtering is off. */
    readonly filterMatches: ReadonlySignal<(columnId: string) => readonly ColumnFilterMatch[]>;
    /** The `raw-formula` clause to AND into the table filter; undefined while nothing applies. */
    readonly formula: ReadonlySignal<string | undefined>;
    /** Columns whose filters apply, which stitched keys tables must carry (AC-FILTER-008). */
    readonly appliedColumnIds: ReadonlySignal<ReadonlySet<string>>;
    /** Labelled applied filters in column order, for the widget header summary. */
    readonly activeFilters: ReadonlySignal<readonly ActiveColumnFilter<string>[]>;
    /** The widget header summary's slice: the active filters plus remove and clear-all events. */
    readonly eventBus: ColumnFiltersEventBus;
}

const NO_COLUMNS: ReadonlySet<string> = new Set();
const NO_FILTERABLE_COLUMNS: ReadonlyMap<string, FilterableColumn> = new Map();
const NO_MATCHES: readonly ColumnFilterMatch[] = [];

/**
 * Derives a table widget's column filter views from its session state. `filterableColumns` maps
 * each eligible column id to its header label and supported matches, in column order. Filters on columns that leave the
 * map or while `enabled` is off stay in state but do not apply.
 */
export function tableColumnFilters(
    model: Pick<ColumnFiltersModel, 'filters' | 'remove' | 'clearAll'>,
    {
        filterableColumns,
        enabled,
    }: {
        filterableColumns: ReadonlySignal<ReadonlyMap<string, FilterableColumn>>;
        enabled: ReadonlySignal<boolean>;
    },
): TableColumnFilters {
    const applicableColumns = computed(() => (enabled.value ? filterableColumns.value : NO_FILTERABLE_COLUMNS));
    const canFilterColumn = computed(() => {
        const columns = applicableColumns.value;
        return (columnId: string) => columns.has(columnId);
    });
    const filterMatches = computed(() => {
        const columns = applicableColumns.value;
        return (columnId: string) => columns.get(columnId)?.matches ?? NO_MATCHES;
    });
    const formula = computed(() => columnFiltersToTableFormula(model.filters.value, canFilterColumn.value));
    const activeFilters = computed(() =>
        enabled.value
            ? getActiveColumnFilters(
                  model.filters.value,
                  [...filterableColumns.value.keys()],
                  Object.fromEntries(
                      [...filterableColumns.value].map(([columnId, column]) => [columnId, column.label]),
                  ),
              )
            : [],
    );
    const appliedColumnIds = computed(() => {
        const ids = new Set(activeFilters.value.map((filter) => filter.columnId));
        return ids.size > 0 ? ids : NO_COLUMNS;
    });
    const eventBus: ColumnFiltersEventBus = EventBusBuilder.create()
        .addSignal('widget::activeColumnFilters', () => activeFilters)
        .addEvent('widget::removeColumnFilter', ({ columnId, filter }) => model.remove(columnId, filter))
        .addEvent('widget::clearAllColumnFilters', () => model.clearAll())
        .build();

    return { enabled, canFilterColumn, filterMatches, formula, appliedColumnIds, activeFilters, eventBus };
}
